import { Component, OnDestroy, OnInit, Optional } from '@angular/core'
import { Router } from '@angular/router'
import { MatDialog, MatDialogRef } from '@angular/material/dialog'
import { MatBottomSheetRef } from '@angular/material/bottom-sheet'
import { select, Store } from '@ngrx/store'
import { forkJoin, Observable, of, Subscription, throwError } from 'rxjs'
import { catchError, map, tap } from 'rxjs/operators'
import { HttpErrorResponse } from '@angular/common/http'
import { AppState, CorpMemberState } from '../../ngrx-store/app.state'
import { selectOnboardingChecklistState } from '../../ngrx-store/selectors/corp.selectors'
import { profileUpdateSuccess } from '../../ngrx-store/actions/corp-member.actions'
import { CallerService } from '../../services/caller.service'
import { PushNotificationService } from '../../services/push-notification.service'
import { ACCOMMODATION_TYPES, NAME_MAX_LENGTH } from '../../utils/constants.util'
import { titleCase } from '../../utils/sale.util'
import { ConfirmActionDialogComponent } from '../confirm-action-dialog/confirm-action-dialog.component'

type WishlistType = 'sale' | 'accommodation'

/** A one-tap wishlist suggestion. `value` is what's saved on the alert and searched for. */
interface WishlistSuggestion {
    label: string
    type: WishlistType
    value: string
}

/** Listings already up that match a wishlist entry, from POST /profile/onboarding/wishlist-matches. */
interface WishlistMatches {
    count: number
    state: string
    search_text: string
}

/** A wishlist item. `id` is set once it's saved as an alert. */
interface WishlistEntry {
    id?: number
    type: WishlistType
    value: string
}

const entryKey = (entry: { type: WishlistType; value: string }) => `${entry.type}:${entry.value}`

/**
 * What most corps members need in their first weeks. Accommodation values must be (lower-cased)
 * ACCOMMODATION_TYPES, or alerts and counts won't find them.
 */
const WISHLIST_SUGGESTIONS: WishlistSuggestion[] = [
    { label: 'Mattress', type: 'sale', value: 'mattress' },
    { label: 'Gas cylinder', type: 'sale', value: 'gas cylinder' },
    { label: 'Fan', type: 'sale', value: 'fan' },
    { label: 'Pots', type: 'sale', value: 'pots' },
    { label: 'Bucket', type: 'sale', value: 'bucket' },
    { label: 'Self-contain', type: 'accommodation', value: 'self contain' },
    { label: 'Flat', type: 'accommodation', value: 'flat' },
]


/**
 * The onboarding checklist (and, once it's done, the corp member's wishlist). Opened by
 * OnboardingChecklistComponent's bar or the nav menu, as a bottom sheet on small screens and a
 * dialog otherwise, so it works with either ref.
 *
 * Works like a form: chip taps (and the name, and the state code) are a local draft, and Save writes
 * all of it (new and removed alerts, the first name, the state code and LGA) at once. The rows:
 *  0. State code and LGA (required) - only for an account without a state code (Google sign-up
 *     doesn't ask for one - needsStateCode()), first, as the wishlist counts and alerts need the
 *     service state. Once saved it's set; changing a state code isn't done here.
 *  1. Wishlist - one-tap chips, each showing what's already listed nearby (a preview count).
 *  2. Notifications (optional - not needed to finish) - only asked once there's a wishlist item,
 *     so the reason is obvious. Immediate rather than saved with the form, since it's a browser
 *     permission prompt.
 *  3. First name (required) - email sign-up doesn't ask for it, to keep sign-up fast (Google fills
 *     it in), and it's what the greeting, emails and chats call them. Pre-filled when we have it.
 * (It used to ask when the service year ends, too - that's moving to the admin side, one less
 * thing for corp members to fill in. service_year_end_date and its reminders stay on the backend.)
 *
 * TODO (decide first whether it's worth building): an in-app "matches" list for wishlist alerts, so
 * a match is visible even when both the push and the email were missed. The backend already records
 * every one (AlertNotifications in corpers.online - see services/alertService.js and issue
 * wachukxs/gh#109); this dialog, as "My wishlist", would be a natural place to show them.
 */
@Component({
    selector: 'app-onboarding-checklist-dialog',
    templateUrl: './onboarding-checklist-dialog.component.html',
    styleUrls: ['./onboarding-checklist-dialog.component.css'],
})
export class OnboardingChecklistDialogComponent implements OnInit, OnDestroy {
    readonly suggestions = WISHLIST_SUGGESTIONS
    readonly accommodationTypes = ACCOMMODATION_TYPES
    readonly nameMaxLength = NAME_MAX_LENGTH

    greeting = 'Welcome to corpers.ng'
    private corpMemberId?: number

    stateCodeDone = true
    /** Shown when the account had no state code - and still once it's saved, as a done step. */
    showStateCodeStep = false
    wishlistDone = false
    nameDone = false
    pushDone = false

    /** The corp member's alerts as saved on the backend. */
    savedEntries: WishlistEntry[] = []
    /** What the chips show - becomes savedEntries on Save. */
    draft: WishlistEntry[] = []
    isLoadingEntries = true
    /** Preview counts by entryKey(); missing means not fetched (or nothing to show). */
    matchesByKey = new Map<string, WishlistMatches>()

    /** Free text for an item; one of ACCOMMODATION_TYPES for a place. */
    customValue = ''
    customType: WishlistType = 'sale'
    showCustomInput = false

    stateCodeDraft = ''
    lgaDraft = ''
    /** The LGAs to pick from: those in the state the state code is from. */
    lgaOptions: string[] = []

    /** The first name as saved; firstNameDraft is what the field shows - becomes it on Save. */
    savedFirstName = ''
    firstNameDraft = ''

    isTogglingPush = false
    /** Whether this browser gets notifications (onboarding's push step is per account, not per device). */
    pushOnThisDevice = false
    /** Why push can't be turned on here (blocked, iPhone without Home Screen, ...), or null. */
    get pushUnavailableHelp(): string | null {
        return this.pushNotificationService.unavailableHelp()
    }

    isSaving = false

    private subscription: Subscription = new Subscription()

    constructor(
        private store: Store<AppState>,
        private callerService: CallerService,
        private router: Router,
        private dialog: MatDialog,
        private pushNotificationService: PushNotificationService,
        @Optional() private dialogRef: MatDialogRef<OnboardingChecklistDialogComponent>,
        @Optional() private bottomSheetRef: MatBottomSheetRef<OnboardingChecklistDialogComponent>,
    ) {
        // Backdrop clicks and Escape go through requestClose(), so unsaved picks aren't lost silently.
        const ref = this.dialogRef ?? this.bottomSheetRef
        if (ref) {
            ref.disableClose = true
            this.subscription.add(ref.backdropClick().subscribe(() => this.requestClose()))
            this.subscription.add(
                ref.keydownEvents().subscribe((event: KeyboardEvent) => {
                    if (event.key === 'Escape') {
                        this.requestClose()
                    }
                }),
            )
        }
    }

    /**
     * The wishlist and the name are required; notifications are optional, so they don't count
     * towards finishing (same rule as onboardingComplete()). Deliberately based on the backend's
     * wishlist stamp, not the current wishlist: removing every item later doesn't
     * reopen onboarding (they've been through it once - the bar would just nag), and this dialog
     * stays reachable as "My wishlist" to add things back.
     */
    get allDone(): boolean {
        return this.stateCodeDone && this.wishlistDone && this.nameDone
    }

    /** The step number shown for a step, counting the state code step only when it's shown. */
    stepNumber(step: 'wishlist' | 'push' | 'name'): number {
        const offset = this.showStateCodeStep ? 1 : 0
        return { wishlist: 1, push: 2, name: 3 }[step] + offset
    }

    get stateCodeInvalid(): boolean {
        return !!this.stateCodeDraft.trim() && !this.callerService.state_code_regex.test(this.stateCodeDraft.trim())
    }

    /** A state code and an LGA in its state, ready to save. */
    get stateCodeReady(): boolean {
        return !this.stateCodeDone && !!this.lgaOptions.length && this.lgaOptions.includes(this.lgaDraft)
    }

    /** Something's already saved, so a disabled Save means "saved", not "nothing to do". */
    get hasSavedAnything(): boolean {
        return this.savedEntries.length > 0
    }

    /** Custom entries (not one of the suggestion chips), shown as their own removable chips. */
    get customEntries(): WishlistEntry[] {
        return this.draft.filter((entry) => !this.suggestions.some((s) => s.type === entry.type && s.value === entry.value))
    }

    get entriesWithMatches(): { entry: WishlistEntry; matches: WishlistMatches }[] {
        return this.draft
            .map((entry) => ({ entry, matches: this.matchesByKey.get(entryKey(entry)) as WishlistMatches }))
            .filter(({ matches }) => (matches?.count ?? 0) > 0)
    }

    /** Whether the notifications ask has something to point at. */
    get hasWishlistItems(): boolean {
        return this.draft.length > 0 || this.wishlistDone
    }

    get draftLabels(): string {
        return this.draft.map((entry) => entry.value).join(', ')
    }

    get savedLabels(): string {
        return this.savedEntries.map((entry) => entry.value).join(', ')
    }

    private get entriesToCreate(): WishlistEntry[] {
        const saved = new Set(this.savedEntries.map(entryKey))
        return this.draft.filter((entry) => !saved.has(entryKey(entry)))
    }

    private get entriesToDelete(): WishlistEntry[] {
        const drafted = new Set(this.draft.map(entryKey))
        return this.savedEntries.filter((entry) => !drafted.has(entryKey(entry)))
    }

    /** A changed, non-blank name - clearing the field doesn't clear the saved one. */
    get firstNameChanged(): boolean {
        const name = this.firstNameDraft.trim()
        return !!name && name !== this.savedFirstName
    }

    get hasChanges(): boolean {
        return this.entriesToCreate.length > 0 || this.entriesToDelete.length > 0 || this.firstNameChanged || this.stateCodeReady
    }

    onStateCodeChange(stateCode: string): void {
        this.stateCodeDraft = stateCode
        this.lgaOptions = this.callerService.lgasForStateCode(stateCode)
        if (!this.lgaOptions.includes(this.lgaDraft)) {
            this.lgaDraft = ''
        }
    }

    ngOnInit(): void {
        this.subscription.add(
            this.store.pipe(select('corper')).subscribe((corper: CorpMemberState) => {
                this.corpMemberId = corper?.id
                // Follow the saved name unless they're mid-edit.
                if (this.firstNameDraft.trim() === this.savedFirstName) {
                    this.firstNameDraft = corper?.first_name ?? ''
                }
                this.savedFirstName = corper?.first_name ?? ''
                const state = corper?.service_state ? titleCase(corper.service_state) : null
                const name = corper?.first_name || corper?.nickname
                this.greeting = state
                    ? `Welcome to ${state}${name ? ', ' + name : ''}`
                    : `Welcome to corpers.ng${name ? ', ' + name : ''}`
            }),
        )

        this.subscription.add(
            this.store.pipe(select(selectOnboardingChecklistState)).subscribe((checklist) => {
                this.stateCodeDone = checklist.stateCodeDone
                this.showStateCodeStep = this.showStateCodeStep || !checklist.stateCodeDone
                this.wishlistDone = checklist.wishlistDone
                this.nameDone = checklist.nameDone
                this.pushDone = checklist.pushDone
            }),
        )

        this.subscription.add(
            this.pushNotificationService.isSubscribed().subscribe((subscribed) => (this.pushOnThisDevice = subscribed)),
        )

        this.callerService.listMyAlerts().subscribe({
            next: (res: any) => {
                this.savedEntries = (res.body.data ?? [])
                    .filter((alert: any) => (alert.type === 'accommodation' ? alert.accommodation_type : alert.item_name))
                    .map((alert: any) => ({
                        id: alert.id,
                        type: alert.type === 'accommodation' ? 'accommodation' : 'sale',
                        value: (alert.type === 'accommodation' ? alert.accommodation_type : alert.item_name).trim().toLowerCase(),
                    }))
                this.draft = [...this.savedEntries]
                this.draft.forEach((entry) => this.fetchMatches(entry))
                this.isLoadingEntries = false
            },
            error: () => {
                this.isLoadingEntries = false
            },
        })
    }

    close(): void {
        this.dialogRef?.close()
        this.bottomSheetRef?.dismiss()
    }

    /** Closes, or first asks whether to throw away picks that haven't been saved. */
    requestClose(then: () => void = () => this.close()): void {
        if (!this.hasChanges) {
            then()
            return
        }
        this.dialog
            .open(ConfirmActionDialogComponent, {
                width: '360px',
                data: {
                    title: 'Discard your changes?',
                    message: 'Some of your changes aren\'t saved yet.',
                    confirmText: 'Discard',
                    cancelText: 'Keep editing',
                    confirmColor: 'warn',
                },
                ariaLabel: 'Confirm discarding unsaved onboarding changes',
            })
            .afterClosed()
            .subscribe((result) => {
                if (result?.confirmed) {
                    then()
                }
            })
    }

    /** Closes right away and snoozes - never waits on the server (see remindAboutOnboardingLater). */
    remindLater(): void {
        this.requestClose(() => {
            this.close()
            this.callerService.remindAboutOnboardingLater()
        })
    }

    // ---- Wishlist (draft until Save)

    isSelected(type: WishlistType, value: string): boolean {
        return this.draft.some((entry) => entry.type === type && entry.value === value)
    }

    toggleSuggestion(suggestion: WishlistSuggestion): void {
        if (this.isSelected(suggestion.type, suggestion.value)) {
            this.removeFromDraft({ type: suggestion.type, value: suggestion.value })
        } else {
            this.addToDraft({ type: suggestion.type, value: suggestion.value })
        }
    }

    setCustomType(type: WishlistType): void {
        this.customType = type
        this.customValue = ''
    }

    addCustom(): void {
        const value = this.customValue.trim().toLowerCase()
        if (!value) {
            return
        }
        if (this.isSelected(this.customType, value)) {
            this.callerService.showNotification('That\'s already on your wishlist.')
            return
        }
        this.addToDraft({ type: this.customType, value })
        this.customValue = ''
    }

    removeFromDraft(entry: WishlistEntry): void {
        this.draft = this.draft.filter((e) => entryKey(e) !== entryKey(entry))
    }

    private addToDraft(entry: WishlistEntry): void {
        // Re-selecting something that was saved keeps its alert rather than making a new one.
        const saved = this.savedEntries.find((e) => entryKey(e) === entryKey(entry))
        this.draft = [...this.draft, saved ?? entry]
        this.fetchMatches(entry)
    }

    private alertBody(entry: WishlistEntry) {
        return entry.type === 'sale'
            ? { type: entry.type, item_name: entry.value }
            : { type: entry.type, accommodation_type: entry.value }
    }

    private fetchMatches(entry: WishlistEntry): void {
        const key = entryKey(entry)
        if (this.matchesByKey.has(key)) {
            return
        }
        this.callerService.previewOnboardingWishlistMatches(this.alertBody(entry)).subscribe({
            next: (res: any) => {
                if (res.body.matches) {
                    this.matchesByKey.set(key, res.body.matches)
                }
            },
            // Just a nice-to-have - the chip still works without a count.
            error: (err: HttpErrorResponse) => console.warn('Failed to preview wishlist matches', err),
        })
    }

    viewMatches(entry: WishlistEntry, matches: WishlistMatches): void {
        const go = () => {
            this.close()
            this.router.navigate(['/search'], {
                queryParams: {
                    tab: entry.type === 'sale' ? 'sales' : 'accommodation',
                    q: matches.search_text,
                    state: matches.state.toUpperCase(), // the search page's state options are upper case
                },
            })
        }
        // Leaving the dialog shouldn't throw away what they picked.
        if (this.hasChanges) {
            this.save().subscribe((ok) => ok && go())
        } else {
            go()
        }
    }

    // ---- Save

    onSave(): void {
        this.save().subscribe((ok) => {
            if (ok) {
                this.callerService.showNotification(this.allDone ? 'Saved. You\'re all set!' : 'Saved.')
            }
        })
    }

    /** Writes the draft (new/removed alerts, the first name, the state code). Emits whether everything saved. */
    private save(): Observable<boolean> {
        const firstName = this.firstNameDraft.trim()
        // The backend's reason when the state code is turned down (taken by another account, ...).
        let stateCodeError: string | null = null
        const requests: Observable<boolean>[] = [
            ...(this.stateCodeReady
                ? [
                    this.callerService.saveOnboardingStateCode(this.stateCodeDraft.trim(), this.lgaDraft).pipe(
                        tap(() => {
                            // The counts were for no state (or none at all) - fetch them for the new one.
                            this.matchesByKey.clear()
                            this.draft.forEach((entry) => this.fetchMatches(entry))
                        }),
                        catchError((err: HttpErrorResponse) => {
                            stateCodeError = err?.error?.message ?? null
                            return throwError(err)
                        }),
                    ),
                ]
                : []),
            ...(this.firstNameChanged
                ? [
                    this.callerService.saveOnboardingFirstName(firstName).pipe(
                        tap((res: any) => {
                            this.firstNameDraft = res.body.data.first_name
                            this.store.dispatch(profileUpdateSuccess({ data: res.body.data }))
                        }),
                    ),
                ]
                : []),
            ...this.entriesToCreate.map((entry) =>
                this.callerService.createOnboardingWishlistAlert(this.alertBody(entry)).pipe(
                    tap((res: any) => {
                        const savedEntry = { ...entry, id: res.body.alert.id }
                        this.savedEntries = [...this.savedEntries, savedEntry]
                        this.draft = this.draft.map((e) => (entryKey(e) === entryKey(entry) ? savedEntry : e))
                        this.store.dispatch(profileUpdateSuccess({ data: res.body.data }))
                    }),
                ),
            ),
            ...this.entriesToDelete.map((entry) =>
                this.callerService.deleteAlert(entry.id as number).pipe(
                    tap(() => (this.savedEntries = this.savedEntries.filter((e) => e.id !== entry.id))),
                ),
            ),
        ].map((request) =>
            request.pipe(
                map(() => true),
                catchError(() => of(false)),
            ),
        )

        if (!requests.length) {
            return of(true)
        }

        this.isSaving = true
        return forkJoin(requests).pipe(
            map((results) => results.every(Boolean)),
            tap((ok) => {
                this.isSaving = false
                if (!ok) {
                    // Whatever did save is already reflected; the rest stays in the draft to retry.
                    this.callerService.showNotification(stateCodeError ?? 'Some changes didn\'t save. Please try again.')
                }
            }),
        )
    }

    // ---- Notifications (immediate - it's a browser permission prompt)

    enablePushNotifications(): void {
        this.isTogglingPush = true
        this.callerService
            .openEnablePushNotificationsDialog()
            .afterClosed()
            .subscribe((result) => {
                if (result === 'declined') {
                    this.markPushStepComplete('Got it — we\'ll email you when something on your wishlist is posted. You can turn on notifications anytime from your profile.')
                } else if (result === 'enabled') {
                    this.markPushStepComplete()
                } else {
                    // Dismissed via Escape/backdrop click without an explicit choice — leave
                    // the step incomplete rather than silently locking in a decision.
                    this.isTogglingPush = false
                }
            })
    }

    /** After the step was answered: turn push on for this device (the step itself stays done). */
    async turnOnPushOnThisDevice(): Promise<void> {
        this.isTogglingPush = true
        await this.pushNotificationService.requestPermissionAndSubscribe() // shows its own error toast
        this.isTogglingPush = false
    }

    declinePushNotifications(): void {
        this.isTogglingPush = true
        this.markPushStepComplete('Got it — we\'ll email you when something on your wishlist is posted. You can turn on notifications anytime from your profile.')
    }

    private markPushStepComplete(declineMessage?: string): void {
        // Asked here, on this device - so NavComponent's "on this device too?" offer doesn't ask again.
        this.pushNotificationService.markOfferedOnThisDevice(this.corpMemberId)
        this.callerService.markOnboardingPushStepComplete().subscribe({
            next: (res: any) => {
                this.isTogglingPush = false
                this.store.dispatch(profileUpdateSuccess({ data: res.body.data }))
                if (declineMessage) {
                    this.callerService.showNotification(declineMessage)
                }
            },
            error: (err: HttpErrorResponse) => {
                this.isTogglingPush = false
                this.callerService.showNotification(err?.error?.message ?? 'Failed to update onboarding step.')
            },
        })
    }

    ngOnDestroy(): void {
        this.subscription.unsubscribe()
    }
}
